<template>
  <l-modal
    :title="t('product-discount.title')"
    :button-text="t('product-discount.save')"
    :button-enabled="discountIsValid"
    :secondary-button="hasExistingDiscount"
    :secondary-button-text="t('product-discount.remove')"
    :secondary-button-enabled="hasExistingDiscount"
    @action="save"
    @secondary-action="removeDiscount"
    @close="emit('close')"
  >
    <l-option-selector v-model="editingDiscount.type" :options="discountTypes" />

    <l-field :label="t('product-discount.discount-to-apply')" class="mt-4">
      <l-number-input
        v-if="editingDiscount.type === 'currency'"
        v-model="editingDiscount.amount"
        type="number"
        :min="0"
        format="currency"
        :icon="discountIcon"
      />
      <l-number-input
        v-else
        v-model="editingDiscount.amount"
        type="number"
        :min="0"
        format="decimal"
        :icon="discountIcon"
      />
    </l-field>

    <l-field :label="t('bill-discount.concept')">
      <l-textarea v-model="editingDiscount.concept" />
    </l-field>
  </l-modal>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { ProductPriceCalculator } from '@last/core'
import type { LIconName } from '@last/core-ui/paprika'
import { LField, LModal, LNumberInput, LOptionSelector, LTextarea } from '@last/core-ui/paprika'

import { useConfirm } from '@/plugins/managerConfirmation'
import { useConfigStore } from '@/store/config'
import { useTabsStore } from '@/store/tabs'
import type { TabProduct } from '@/types'

const { t } = useI18n()
const configStore = useConfigStore()
const tabsStore = useTabsStore()

const confirm = useConfirm()

const props = defineProps<{
  products: TabProduct[]
}>()

const emit = defineEmits<{
  close: []
}>()

const { currencyIcon } = storeToRefs(configStore)

const editingDiscount = ref<{
  type: string
  amount: number | null
  concept: string | null
}>({
  type: 'percentage',
  amount: null,
  concept: null
})

onMounted(() => {
  const firstProduct = props.products[0]
  const allHaveSameDiscount = props.products.every(
    product =>
      product.discountType === firstProduct.discountType &&
      product.discountAmount === firstProduct.discountAmount &&
      product.discountConcept === firstProduct.discountConcept
  )

  if (allHaveSameDiscount && firstProduct.discountType) {
    editingDiscount.value.type = firstProduct.discountType
    editingDiscount.value.amount =
      firstProduct.discountType === 'cash'
        ? firstProduct.discountAmount! / 100
        : firstProduct.discountAmount!
    editingDiscount.value.concept = firstProduct.discountConcept ?? null
  }
})

const discountIcon = computed<LIconName>(() => {
  if (editingDiscount.value.type === 'percentage') {
    return 'percentage'
  }
  return currencyIcon.value
})

const discountTypes = computed(() => [
  {
    icon: currencyIcon.value as LIconName,
    value: 'cash',
    label: t('product-discount.cash')
  },
  {
    icon: 'percentage' as LIconName,
    value: 'percentage',
    label: t('product-discount.percentage')
  }
])

const discountIsValid = computed(() => {
  if (
    editingDiscount.value === null ||
    editingDiscount.value.amount === null ||
    isNaN(editingDiscount.value.amount) ||
    editingDiscount.value.type === null
  ) {
    return false
  }

  if (editingDiscount.value.type === 'cash') {
    return props.products.every(product => {
      return editingDiscount.value.amount! * 100 <= product.fullPrice
    })
  }

  if (editingDiscount.value.type === 'percentage') {
    return editingDiscount.value.amount <= 100
  }

  return true
})

const hasExistingDiscount = computed(() => {
  return props.products.some(product => product.discountType && product.discountAmount)
})

async function save() {
  if (!discountIsValid.value) return
  const hasPrivilege = await confirm('DISCOUNT_MANAGER')
  if (!hasPrivilege) return

  const sendingAmount =
    editingDiscount.value.type === 'cash'
      ? editingDiscount.value.amount! * 100
      : editingDiscount.value.amount

  updateDiscount({ ...editingDiscount.value, amount: sendingAmount })
  emit('close')
}

async function updateDiscount(discount: any) {
  if (+discount.amount === 0) {
    removeProductDiscount()
    return
  }

  for (const product of props.products) {
    const productPricing = ProductPriceCalculator.calculateProductPricing({
      ...product,
      discountType: discount.type,
      discountAmount: discount.amount
    })

    if (product.quantity > 1) {
      const splittedProductId = tabsStore.splitProduct({
        productId: product.id,
        seat: product.seat
      })
      tabsStore.updateProductDiscount({
        productId: splittedProductId as string,
        discount: {
          discountType: discount.type,
          discountAmount: discount.amount,
          discountConcept: discount.concept,
          promotionId: null
        },
        productPricing: {
          ...productPricing
        }
      })
    } else {
      tabsStore.updateProductDiscount({
        productId: product.id,
        discount: {
          discountType: discount.type,
          discountAmount: discount.amount,
          discountConcept: discount.concept,
          promotionId: null
        },
        productPricing: {
          ...productPricing
        }
      })
    }
  }
}

function removeProductDiscount() {
  for (const product of props.products) {
    const productPricing = ProductPriceCalculator.calculateProductPricing({
      ...product,
      discountType: undefined,
      discountAmount: undefined
    })
    tabsStore.updateProductDiscount({
      productId: product.id,
      discount: {
        discountType: null,
        discountAmount: null,
        discountConcept: null,
        promotionId: null
      },
      productPricing: {
        ...productPricing
      }
    })
  }
}

async function removeDiscount() {
  const hasPrivilege = await confirm('DISCOUNT_MANAGER')
  if (!hasPrivilege) return

  removeProductDiscount()
  emit('close')
}
</script>
